import { basicInfoAtom, cartAtom, deliveryServiceAtom } from "@/atoms"
import deliveryAreaConfigAtom from "@/atoms/delivery-area-config"
import schedulingContextAtom from "@/atoms/scheduling-context"
import { useStorefrontScheduling } from "@/hooks/use-storefront-scheduling"
import type { ScheduleSlotsAddress } from "@/lib/scheduling/fetch-schedule-slots"
import type { ScheduleSlot } from "@/lib/scheduling/schedule-slots"
import { findChannel } from "@/lib/scheduling/storefront-state"
import type { ScheduledPaymentPolicy, SchedulingSlot } from "@/lib/scheduling/types"
import { useAtom, useAtomValue } from "jotai"
import { useCallback, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { useCheckoutContext } from "../CheckoutContext"
import { FutureWindowsSlotPicker } from "../components/FutureWindowsSlotPicker"
import { ScheduledSlotPicker } from "../components/ScheduledSlotPicker"
import { StepFooter } from "../components/StepFooter"
import { StepHeader } from "../components/StepHeader"
import { PageContainer } from "@/components/ui"
import { useEnforceWizardEntry } from "../hooks/useEnforceWizardEntry"
import { evaluateAddresses } from "../lib/evaluate-addresses"
import { checkoutAtom, isScheduleStepVisible, stepGuards } from "../state"

/**
 * The slot choice, on its own step: "Como receber" answers the channel, this
 * one answers the hour. Only exists for a scheduled order whose channel takes
 * scheduling — anything else is sent back to the channel step.
 */
export function ScheduleStep() {
  const { addresses } = useCheckoutContext()
  const cart = useAtomValue(cartAtom)
  const basicInfo = useAtomValue(basicInfoAtom)
  const deliveryService = useAtomValue(deliveryServiceAtom)
  const deliveryAreaConfig = useAtomValue(deliveryAreaConfigAtom)
  const schedulingContext = useAtomValue(schedulingContextAtom)
  const scheduling = useStorefrontScheduling()
  const [state, setState] = useAtom(checkoutAtom)
  const navigate = useNavigate()

  useEnforceWizardEntry("schedule", addresses)

  const stepVisible = isScheduleStepVisible(
    state,
    scheduling.schedulableChannels.map((channel) => channel.serviceType),
  )

  useEffect(() => {
    if (scheduling.known && !stepVisible) {
      navigate("/cart/checkout/service", { replace: true })
    }
  }, [scheduling.known, stepVisible, navigate])

  const evaluated = useMemo(
    () =>
      evaluateAddresses(addresses, deliveryService?.serviceArea, basicInfo?.address, deliveryAreaConfig),
    [addresses, deliveryService?.serviceArea, basicInfo?.address, deliveryAreaConfig],
  )

  const activeChannel = state.serviceType ? findChannel(schedulingContext, state.serviceType) : null

  const selectSlot = useCallback(
    (slot: SchedulingSlot) => {
      setState((prev) => ({ ...prev, scheduledSlot: slot }))
    },
    [setState],
  )

  const selectFutureSlot = useCallback(
    (slot: ScheduleSlot, date: string) => {
      setState((prev) => ({
        ...prev,
        scheduledSlot: { readyAt: slot.readyStart, readyEndAt: slot.readyEnd },
        scheduledArrival: { start: slot.arrivalStart, end: slot.arrivalEnd },
        scheduledDate: date,
      }))
    },
    [setState],
  )

  const selectFutureDate = useCallback(
    (date: string) => {
      setState((prev) =>
        prev.scheduledDate === date
          ? prev
          : { ...prev, scheduledDate: date, scheduledSlot: null, scheduledArrival: null },
      )
    },
    [setState],
  )

  const applyFuturePayment = useCallback(
    (payment: ScheduledPaymentPolicy) => {
      setState((prev) =>
        prev.scheduledPayment === payment ? prev : { ...prev, scheduledPayment: payment },
      )
    },
    [setState],
  )

  const selectedFutureSlot: ScheduleSlot | null =
    state.scheduledSlot && state.scheduledArrival
      ? {
          arrivalStart: state.scheduledArrival.start,
          arrivalEnd: state.scheduledArrival.end,
          readyStart: state.scheduledSlot.readyAt,
          readyEnd: state.scheduledSlot.readyEndAt,
        }
      : null

  const chosenAddress = evaluated.find((a) => a.address.id === state.addressId)?.address
  const futureAddress: ScheduleSlotsAddress | null =
    state.serviceType === "DELIVERY" && chosenAddress
      ? {
          lat: chosenAddress.latitude,
          lng: chosenAddress.longitude,
          district: chosenAddress.district,
          city: chosenAddress.city,
          state: chosenAddress.state,
        }
      : null

  const validation = stepGuards.schedule({
    state,
    cartTotal: cart.totalPrice,
    hasDeliverableAddress: () => true,
    scheduleStepVisible: true,
  })

  return (
    <>
      <StepHeader
        title={
          state.serviceType === "DELIVERY"
            ? "Quando você quer receber?"
            : "Quando você quer retirar?"
        }
        description="Escolha um dos horários que a loja oferece para este canal."
      />

      <PageContainer as="section" className="space-y-6">
        {scheduling.futureWindows && state.serviceType ? (
          <FutureWindowsSlotPicker
            serviceType={state.serviceType}
            address={futureAddress}
            selectedDate={state.scheduledDate}
            selected={selectedFutureSlot}
            onSelectDate={selectFutureDate}
            onSelectSlot={selectFutureSlot}
            onPaymentPolicy={applyFuturePayment}
          />
        ) : activeChannel ? (
          <ScheduledSlotPicker
            channel={activeChannel}
            selected={state.scheduledSlot}
            onSelect={selectSlot}
            deliveryEtaMinutes={
              state.serviceType === "DELIVERY"
                ? evaluated.find((a) => a.address.id === state.addressId)?.estimateMinutes
                : undefined
            }
          />
        ) : null}
      </PageContainer>

      <StepFooter
        onBack={() => navigate("/cart/checkout/service", { replace: true })}
        continueLabel="Continuar"
        continueDisabled={!validation.ok}
        continueHint={validation.hint}
        onContinue={() => navigate("/cart/checkout/payment")}
      />
    </>
  )
}
